/* eslint-disable @typescript-eslint/member-ordering */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { INDIVIDUAL, SALES } from '@rma/common-ui-domain/types';
import { nonNullable } from '@rma/common-ui-rxjs/operators';
import { LocationType } from '@rma/generic/domain/types';
import { SubmittingStatus } from '@rma/generic/ui/forms/submitting';
import { RmaIcons } from '@rma/generic/ui/icon';
import { FeatureFlags, FeatureFlagService } from '@rma/generic/util/feature-flags';
import { getProfileContactAnalytics } from '@rma/profiles/private/util-analytics';
import { ReviewApiService } from '@rma/reviews/public/api';
import type { ReviewSearchFilter, SearchReview } from '@rma/reviews/public/domain';
import { concatMap, filter, map, Observable, switchMap, tap, withLatestFrom } from 'rxjs';
import type { BedroomsOptionType } from '../domain/bedroom-options';
import type { ContactRequest } from '../domain/contact';
import type { ImportantOptionType } from '../domain/important-options';
import { PropertyTypes, PropertyTypesIcons } from '../domain/property-types';
import {
  FindMyAgentRequest,
  FindMyAgentResponse,
  FindMyAgentResponseVM,
  mapStats,
  OVERALL_LOCATION_ID,
  RankingAttributionBackgroundColor,
  RankingAttributionTextColor,
} from '../domain/results';
import type { WhenToSellOptionType } from '../domain/when-to-sell-options';
import { FindMyAgentService } from '../services/find-my-agent.service';

const steps = ['whyareyouhere', 'propertytype', 'bedrooms', 'whentosell', 'address', 'important', 'results', 'renting', 'buying'] as const;
export type Step = (typeof steps)[number];

export type View = 'block' | 'table';

interface FindMyAgentState {
  steps: string[];
  propertyType?: PropertyTypes;
  propertyTypeLabel?: string;
  bedrooms?: BedroomsOptionType;
  whenToSell?: WhenToSellOptionType;
  locationId?: number;
  localityName?: string;
  locationType?: LocationType;
  addressId?: number;
  address?: string;
  importantCriterion?: ImportantOptionType;
  otherImportantReason?: string | null;
  nextStep?: Step;
  loading: boolean;
  error?: string;
  agents: FindMyAgentResponseVM[];
  showing: number;
  activeLocality: string;
  view: View;
  contact?: string;
  contactSubmitting: SubmittingStatus | null;
  contactError?: string;
  saving: boolean;
  saved?: number;
  savedCriteria?: Params;
}

@Injectable()
export class FindMyAgentStore extends ComponentStore<FindMyAgentState> {
  private readonly findMyAgentService = inject(FindMyAgentService);
  private readonly reviewsService = inject(ReviewApiService);
  private readonly featureService = inject(FeatureFlagService);

  readonly propertyType$ = this.select(({ propertyType }) => propertyType);
  readonly propertyTypeLabel$ = this.select(this.propertyType$, (propertyType) => `property-types.${propertyType}`);
  readonly bedrooms$ = this.select(({ bedrooms }) => bedrooms);
  readonly whenToSell$ = this.select(({ whenToSell }) => whenToSell);
  readonly addressId$ = this.select(({ addressId }) => addressId);
  readonly address$ = this.select(({ address }) => address);
  readonly locationId$ = this.select(({ locationId }) => locationId);
  readonly localityName$ = this.select(({ localityName }) => localityName);
  readonly locationType$ = this.select(({ locationType }) => locationType);
  readonly importantCriterion$ = this.select(({ importantCriterion }) => importantCriterion);
  readonly otherImportantReason$ = this.select(({ otherImportantReason }) => otherImportantReason);
  readonly nextStep$ = this.select(({ nextStep }) => nextStep);

  readonly queryParams$ = this.select({
    propertyType: this.propertyType$,
    bedrooms: this.bedrooms$,
    whenToSell: this.whenToSell$,
    addressId: this.addressId$,
    locationId: this.locationId$,
    localityName: this.localityName$,
    importantCriterion: this.importantCriterion$,
    otherImportantReason: this.otherImportantReason$,
    locationType: this.locationType$,
  }).pipe(
    map((queryParams) => {
      const params: Params = {};

      (Object.keys(queryParams) as Array<keyof typeof queryParams>).map((key) => {
        const value = queryParams[key];
        if (value) {
          params[key] = value;
        }
      });

      return params;
    }),
  );

  readonly criteria$ = this.select({
    propertyType: this.propertyType$.pipe(filter(Boolean)),
    bedrooms: this.bedrooms$.pipe(filter(Boolean)),
    locationId: this.locationId$.pipe(filter(Boolean)),
    locationType: this.locationType$,
    addressId: this.addressId$,
    localityName: this.localityName$,
    address: this.address$,
    importantCriterion: this.importantCriterion$,
    otherImportantReason: this.otherImportantReason$,
    whenToSell: this.whenToSell$,
  });

  readonly loading$ = this.select(({ loading }) => loading);
  readonly error$ = this.select(({ error }) => error);

  private readonly agents$ = this.select(({ agents }) => agents);
  private readonly showing$ = this.select(({ showing }) => showing);
  readonly results$ = this.select(this.agents$, this.showing$, (agents, showing) => agents.slice(0, showing));
  readonly canShowMore$ = this.select(this.agents$, this.showing$, (agents, showing) => agents.length > showing);

  readonly locality$ = this.select({
    value: this.locationId$.pipe(
      nonNullable(),
      map((id: number) => id.toString()),
    ),
    label: this.localityName$.pipe(nonNullable()),
  });
  readonly activeLocality$ = this.select(({ activeLocality }) => activeLocality);

  readonly resultsState$ = this.select({
    locality: this.locality$,
    activeLocality: this.activeLocality$,
  });
  readonly view$ = this.select(({ view }) => view);

  readonly contact$ = this.select(
    this.results$,
    this.select(({ contact }) => contact),
    (results, contact) => results.find((r) => r.profileCode === contact),
  );
  readonly contactSubmitting$ = this.select(({ contactSubmitting }) => contactSubmitting);
  readonly contactError$ = this.select(({ contactError }) => contactError);

  readonly saving$ = this.select(({ saving }) => saving);
  readonly saved$ = this.select(({ saved }) => saved);
  readonly savedCriteria$ = this.select(({ savedCriteria }) => savedCriteria);
  steps = steps;

  readonly propertyTypes$ = this.featureService.getFeature$<PropertyTypes[]>(FeatureFlags.FindMyAgentPropertyTypes, []).pipe(
    map((types: PropertyTypes[]) => {
      return types.reduce<Record<string, RmaIcons>>((acc, item) => ({ ...acc, [item]: PropertyTypesIcons[item] }), {});
    }),
  );

  constructor() {
    super({
      loading: false,
      agents: [],
      showing: 0,
      activeLocality: OVERALL_LOCATION_ID,
      view: 'block',
      contactSubmitting: null,
      saving: false,
      steps: steps.filter((value) => value === 'renting'),
    });

    const criteria = this.findMyAgentService.restore();
    this.patchState({ savedCriteria: criteria });
  }

  public readonly getResultsEffect = this.effect((request$: Observable<FindMyAgentRequest>) =>
    request$.pipe(
      tap(() => this.patchState({ loading: true, agents: [] })),
      switchMap((request) =>
        this.findMyAgentService.getAgents(request).pipe(
          tapResponse(
            (agents) =>
              this.patchState({
                agents: agents.map((agent, index) => this.mapAgent(agent, index, request)),
                loading: false,
                showing: agents.length < 3 ? agents.length : 3,
              }),
            (error: HttpErrorResponse) => {
              console.error(error);
              this.patchState({ loading: false, error: this.getError(error) });
            },
          ),
        ),
      ),
    ),
  );

  public readonly getLatestReviewEffect = this.effect((profileCode$: Observable<string>) =>
    profileCode$.pipe(
      tap((profileCode) => this.setLatestReviewsStatus({ profileCode, error: undefined, loading: true })),
      map((profileCode) => {
        const reviewFilter: ReviewSearchFilter = {
          profiles: [{ profileCode, profileType: INDIVIDUAL }],
          take: 5,
          verticalTypes: [SALES],
        };

        return reviewFilter;
      }),
      concatMap((reviewFilter) =>
        this.reviewsService.getReviews(reviewFilter).pipe(
          tapResponse(
            (reviews) =>
              this.setLatestReviews({ profileCode: reviewFilter?.profiles?.[0].profileCode ?? '', latestReview: reviews.results }),
            (error: HttpErrorResponse) => {
              console.error(error);
              this.setLatestReviewsStatus({
                profileCode: reviewFilter?.profiles?.[0].profileCode ?? '',
                error: this.getError(error),
                loading: false,
              });
            },
          ),
        ),
      ),
    ),
  );

  public readonly sendContactEffect = this.effect((request$: Observable<ContactRequest>) =>
    request$.pipe(
      tap(() => this.patchState({ contactSubmitting: 'Submitting', contactError: undefined })),
      withLatestFrom(this.contact$),
      switchMap(([request, contact]) =>
        this.findMyAgentService.contact(SALES, INDIVIDUAL, contact?.profileCode ?? '', request).pipe(
          tapResponse(
            () => this.patchState({ contactSubmitting: 'Success', contactError: undefined }),
            (error: HttpErrorResponse) => {
              console.error(error);
              this.patchState({ contactSubmitting: 'Error', contactError: this.getError(error) });
            },
          ),
        ),
      ),
    ),
  );

  public readonly saveEffect = this.effect((notification$: Observable<boolean>) =>
    notification$.pipe(
      tap(() => this.patchState({ saving: true, saved: undefined })),
      withLatestFrom(this.queryParams$),
      map(([notification, criteria]) => {
        this.findMyAgentService.save(criteria);
        this.patchState({ saving: false, saved: notification ? Date.now() : undefined, savedCriteria: criteria });
      }),
    ),
  );

  public readonly clearSaveEffect = this.effect((clear$: Observable<boolean>) =>
    clear$.pipe(
      tap(() => {
        this.patchState({ savedCriteria: undefined, saved: undefined });
        this.findMyAgentService.clear();
      }),
    ),
  );

  public readonly setView = this.updater((state, view: View) => ({
    ...state,
    view,
  }));

  public readonly setShowActivity = this.updater((state, activity: { profileCode: string; showActivity: boolean }) => ({
    ...state,
    agents: this.updateAgent(state.agents, activity.profileCode, { showActivity: activity.showActivity }),
  }));

  public readonly showMore = this.updater((state) => ({
    ...state,
    showing: state.showing + 3,
  }));

  private readonly setLatestReviews = this.updater((state, review: { profileCode: string; latestReview: SearchReview[] }) => ({
    ...state,
    agents: this.updateAgent(state.agents, review.profileCode, { latestReviews: review.latestReview, latestReviewsLoading: false }),
  }));

  private readonly setLatestReviewsStatus = this.updater((state, status: { profileCode: string; loading: boolean; error?: string }) => ({
    ...state,
    agents: this.updateAgent(state.agents, status.profileCode, {
      latestReviewsError: status.error,
      latestReviewsLoading: status.loading,
    }),
  }));

  public readonly restore = this.updater((state, activity: { profileCode: string; showActivity: boolean }) => ({
    ...state,
    agents: this.updateAgent(state.agents, activity.profileCode, { showActivity: activity.showActivity }),
  }));

  private mapAgent(agent: FindMyAgentResponse, index: number, request: FindMyAgentRequest): FindMyAgentResponseVM {
    const vm: FindMyAgentResponseVM = {
      ...agent,
      attribution: {
        background: RankingAttributionBackgroundColor[index % 3],
        text: RankingAttributionTextColor[index % 3],
      },
      showAttribution: index < 3,
      overallStatistics: mapStats(agent.overallStats),
      locationStatistics: mapStats(agent.locationStats),
      showActivity: false,
      contactAnalytics: getProfileContactAnalytics(SALES, agent.profileCode, INDIVIDUAL, agent.isMobile, 'FindMyAgent'),
      propertyType: request.propertyType,
      localityName: request.localityName,
      locationId: request.locationId,
      locationType: request.locationType,
      address: request.address,
      addressId: request.addressId,
    };

    return vm;
  }

  private updateAgent(
    state: FindMyAgentResponseVM[],
    profileCode: string,
    agentState: Partial<FindMyAgentResponseVM>,
  ): FindMyAgentResponseVM[] {
    const index = state.findIndex((a) => a.profileCode === profileCode);

    const newState = [...state];
    newState[index] = { ...newState[index], ...agentState };

    return newState;
  }

  private getError(error: HttpErrorResponse): string {
    console.error(error);

    return error.error?.message ?? error.message ?? '';
  }
}
